import { createFileRoute, Link } from "@tanstack/react-router";
import heroAsset from "@/assets/hero-v2.jpg.asset.json";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { plans } from "@/data/site-content";
import { CurrencyToggle, useCurrency } from "@/hooks/use-currency";
import { button3DClass } from "@/components/button-3d";

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Our Pricing Plans in India | MG Content Labs" },
      {
        name: "description",
        content:
          "Digital marketing agency pricing in India: Baseline ₹9,990/mo (~$119), Growth ₹17,990/mo (~$215), plus custom Enterprise plans. No hidden fees.",
      },
      { property: "og:title", content: "Our Pricing Plans in India | MG Content Labs" },
      { property: "og:description", content: "Transparent digital marketing agency pricing in INR and USD — Baseline, Growth, and custom Enterprise plans." },
      { property: "og:url", content: "https://mgcontentlabs.com/pricing" },
      { property: "og:image", content: `https://mgcontentlabs.com${heroAsset.url}` },
      { name: "twitter:image", content: `https://mgcontentlabs.com${heroAsset.url}` },
    ],
    links: [{ rel: "canonical", href: "https://mgcontentlabs.com/pricing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: PricingPage,
});

const faqs = [
  {
    q: "How much do our services cost in India?",
    a: "Our Baseline plan is ₹9,990 per month (about $119) and the Growth plan is ₹17,990 per month (about $215). Enterprise engagements are scoped individually. All plans are fixed monthly retainers with no setup fee.",
  },
  {
    q: "What is included in a digital marketing agency retainer?",
    a: "Each retainer bundles SEO content strategy, content writing, on-page content optimization, and distribution. The Growth plan adds deeper answer engine optimization work and digital PR support.",
  },
  {
    q: "How long until I see results?",
    a: "Most clients see meaningful ranking movement in 90 days and material traffic lifts within 6 months. Digital marketing compounds — the biggest gains typically arrive in months 9–18.",
  },
  {
    q: "Can I switch plans?",
    a: "Yes. Upgrade, downgrade, or pause anytime with 30 days' notice. Your editor works with you to re-scope the deliverables.",
  },
  {
    q: "Do you write in my brand voice?",
    a: "Every engagement starts with a voice audit and messaging brief. We ghostwrite in your voice, not ours.",
  },
  {
    q: "What if I need something custom?",
    a: "The Enterprise plan is fully bespoke. Book a call and we'll scope a plan around your goals, capacity, and existing team.",
  },
];

function PricingPage() {
  const { currency } = useCurrency();
  return (
    <div className="min-h-screen bg-white text-brand-deep">
      <SiteNav />

      <header className="bg-brand-muted py-24 text-center">
        <div className="mx-auto max-w-3xl px-6">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">Pricing</span>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl">
            Our pricing plans <span className="italic text-brand-accent">in India.</span>
          </h1>
          <p className="mt-6 text-lg text-brand-deep/70">
            Fixed monthly digital marketing retainers in INR and USD. No hidden fees. Cancel anytime.
          </p>
          <div className="mt-8 flex justify-center">
            <CurrencyToggle />
          </div>
        </div>
      </header>

      <section className="bg-brand-deep py-24 text-white">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="mb-12 text-center font-serif text-4xl">
            Digital marketing plans &amp; pricing
          </h2>
          <div className="grid gap-8 lg:grid-cols-3">
            {plans.map((plan) => (
              <div
                key={plan.slug}
                id={plan.slug}
                className={
                  plan.featured
                    ? "relative flex scroll-mt-24 flex-col border border-brand-accent bg-white p-10 text-brand-deep shadow-2xl lg:scale-105"
                    : "flex scroll-mt-24 flex-col border border-white/10 bg-white/5 p-10"
                }
              >
                {plan.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand-accent px-4 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                    Recommended
                  </div>
                )}
                <h3 className="text-lg font-bold uppercase tracking-widest">{plan.name}</h3>
                <p className={plan.featured ? "mt-2 text-sm text-brand-deep/60" : "mt-2 text-sm text-white/60"}>
                  {plan.tagline}
                </p>
                <div className="mt-6 flex items-baseline">
                  <span className="text-4xl font-bold italic tabular-nums transition-all">
                    {currency === "USD" ? plan.price.usd : plan.price.inr}
                  </span>
                  {plan.cadence && (
                    <span className={plan.featured ? "ml-2 text-brand-deep/80" : "ml-2 text-white/80"}>
                      {plan.cadence}
                    </span>
                  )}
                </div>
                <ul className={plan.featured ? "mt-8 mb-10 flex-1 space-y-4 text-sm text-brand-deep/70" : "mt-8 mb-10 flex-1 space-y-4 text-sm text-white/70"}>
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="mt-1 text-brand-accent">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {plan.ctaKind === "checkout" ? (
                  <Link
                    to="/checkout"
                    search={{ plan: plan.slug, mode: "monthly" as const }}
                    className={
                      plan.featured
                        ? button3DClass("accent", "default", true)
                        : button3DClass("outlineWhite", "default", true)
                    }
                  >
                    {plan.cta}
                  </Link>
                ) : (
                  <Link
                    to="/contact"
                    search={{ plan: plan.slug }}
                    className={
                      plan.featured
                        ? button3DClass("accent", "default", true)
                        : button3DClass("outlineWhite", "default", true)
                    }
                  >
                    {plan.cta}
                  </Link>
                )}
                {plan.ctaKind === "checkout" && (
                  <p className={plan.featured ? "mt-4 text-center text-[10px] uppercase tracking-widest text-brand-deep/80" : "mt-4 text-center text-[10px] uppercase tracking-widest text-white/80"}>
                    Pay securely with PayPal · Cards accepted · Cancel anytime
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="font-serif text-4xl">Pricing FAQ</h2>
          <div className="mt-12 divide-y divide-brand-deep/10">
            {faqs.map((f) => (
              <div key={f.q} className="py-6">
                <h3 className="font-bold">{f.q}</h3>
                <p className="mt-2 text-sm text-brand-deep/70">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
